/**
 * IndexedDB storage for the checkpoint journal (journal.js).
 * Kept in its own database so chat history (chatDb.js) never needs a schema
 * migration for it.
 *
 * checkpoints: { id, projectId, chatId, messageId, reason, createdAt,
 *                fingerprint, type, baseId, chainId, bytes, data }
 * meta:        { projectId, fingerprint, updatedAt, detachedAt? }
 */

const DB_NAME = "cc-assistant-checkpoints";
const DB_VERSION = 1;
const CHECKPOINT_STORE = "checkpoints";
const META_STORE = "meta";

let dbPromise = null;
let persistRequested = false;

function requestToPromise(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/** Ask the browser not to evict checkpoints under storage pressure (Safari clears idle sites). */
function requestPersistence() {
    if (persistRequested) return;
    persistRequested = true;
    navigator.storage?.persist?.().catch(() => {});
}

function openDb() {
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(CHECKPOINT_STORE)) {
                    const store = db.createObjectStore(CHECKPOINT_STORE, { keyPath: "id" });
                    store.createIndex("projectId", "projectId", { unique: false });
                }
                if (!db.objectStoreNames.contains(META_STORE)) {
                    db.createObjectStore(META_STORE, { keyPath: "projectId" });
                }
            };
            request.onsuccess = () => {
                const db = request.result;
                db.onversionchange = () => {
                    db.close();
                    dbPromise = null;
                };
                resolve(db);
            };
            request.onerror = () => {
                dbPromise = null;
                reject(request.error);
            };
        });
    }
    return dbPromise;
}

async function withStore(storeName, mode, callback) {
    const db = await openDb();
    const tx = db.transaction(storeName, mode);
    const done = new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
    });
    const [result] = await Promise.all([callback(tx.objectStore(storeName)), done]);
    return result;
}

/** @type {import("./journal.js").JournalStorage} */
export const checkpointStorage = {
    async putCheckpoint(record) {
        requestPersistence();
        await withStore(CHECKPOINT_STORE, "readwrite", (store) => requestToPromise(store.put(record)));
    },
    getCheckpoint(id) {
        return withStore(CHECKPOINT_STORE, "readonly", (store) => requestToPromise(store.get(id)));
    },
    async listCheckpoints(projectId) {
        if (!projectId) return [];
        const records = await withStore(CHECKPOINT_STORE, "readonly", (store) =>
            requestToPromise(store.index("projectId").getAll(projectId)),
        );
        return records.map(({ data, ...record }) => record);
    },
    async deleteCheckpoints(ids) {
        await withStore(CHECKPOINT_STORE, "readwrite", (store) =>
            Promise.all(ids.map((id) => requestToPromise(store.delete(id)))),
        );
    },
    getMeta(projectId) {
        if (!projectId) return Promise.resolve(undefined);
        return withStore(META_STORE, "readonly", (store) => requestToPromise(store.get(projectId)));
    },
    async putMeta(meta) {
        await withStore(META_STORE, "readwrite", (store) => requestToPromise(store.put(meta)));
    },
};
